import { Controller } from "@hotwired/stimulus"

/**
 * Loads the unsaved certificate PDF preview via fetch on the parent
 * page and displays it in an iframe as a blob URL.
 *
 * A nested iframe form PATCH is not a top-level navigation, so a
 * SameSite=Lax session cookie is omitted and Rails rejects the CSRF
 * token with 422. Same-origin fetch from the parent sends the cookie.
 */
export default class extends Controller {
  static targets = ["frame", "error"]
  static values = {
    url: String,
    error: String
  }

  disconnect() {
    this.#revokeObjectUrl()
  }

  preview(event) {
    event.preventDefault()
    if (this.previewing) return

    const form = this.element.querySelector("form")
    if (!form || !this.hasFrameTarget) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content ||
      form.querySelector('input[name="authenticity_token"]')?.value
    const headers = {
      Accept: "application/pdf",
      "X-Requested-With": "XMLHttpRequest"
    }
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken

    this.previewing = true
    const button = event.currentTarget
    button.disabled = true
    this.#hideError()

    fetch(this.urlValue, {
      method: "PATCH",
      body: new FormData(form),
      credentials: "same-origin",
      headers
    })
      .then((response) => this.#applyResponse(response))
      .catch(() => this.#showError(this.errorValue))
      .finally(() => {
        this.previewing = false
        button.disabled = false
      })
  }

  async #applyResponse(response) {
    const contentType = response.headers.get("content-type") || ""
    if (response.ok && contentType.includes("application/pdf")) {
      const blob = await response.blob()
      this.#revokeObjectUrl()
      this.objectUrl = URL.createObjectURL(blob)
      this.frameTarget.src = this.objectUrl
      return
    }

    if (contentType.includes("text/plain")) {
      const text = (await response.text()).trim()
      this.#showError(text || this.errorValue)
      return
    }

    this.#showError(this.errorValue)
  }

  #showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  #hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  #revokeObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
  }
}
